import { Circle, Hand, Pentagon } from 'lucide-react'
import React, { forwardRef } from 'react'
import type { Tool } from './useDrawnZoneDrawing'

interface Props {
  tool: Tool
  onToolChange: (next: Tool) => void
}

// Toolbar lives inside the map via Google Maps `map.controls[TOP_CENTER]`.
// The hook holds a ref to this node and pushes it into the control slot after
// the map initialises. Outside of the map (before init) the node is rendered
// off-screen so React still mounts the DOM children.
export const DrawnZoneToolbar = forwardRef<HTMLDivElement, Props>(
  ({ tool, onToolChange }, ref) => (
    <div
      ref={ref}
      style={{
        marginTop: 10,
        display: 'flex',
        gap: 3,
        background: '#fff',
        borderRadius: 5,
        padding: 2,
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
      }}
    >
      <ToolButton
        active={tool === 'pan'}
        label="Pan"
        onClick={() => onToolChange('pan')}
      >
        <Hand size={16} />
      </ToolButton>
      <ToolButton
        active={tool === 'polygon'}
        label="Draw polygon"
        onClick={() => onToolChange('polygon')}
      >
        <Pentagon size={16} />
      </ToolButton>
      <ToolButton
        active={tool === 'circle'}
        label="Draw circle"
        onClick={() => onToolChange('circle')}
      >
        <Circle size={16} />
      </ToolButton>
    </div>
  ),
)
DrawnZoneToolbar.displayName = 'DrawnZoneToolbar'

const ToolButton: React.FC<{
  active: boolean
  label: string
  onClick: () => void
  children: React.ReactNode
}> = ({ active, label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    aria-pressed={active}
    onClick={onClick}
    style={{
      width: 28,
      height: 28,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 4,
      border: 'none',
      background: 'transparent',
      color: active ? '#2563EB' : '#7F8C8D',
      opacity: active ? 1 : 0.6,
      cursor: 'pointer',
      transition: 'opacity 120ms, color 120ms',
    }}
  >
    {children}
  </button>
)
